import httpClient, { createRequestWithFallback } from "./httpClient";
import { API_ENDPOINTS } from "../../constants/endpoints";
import mockApiService from "./mockApiService";

/**
 * Servicio optimizado para operaciones de cuentas
 */
class AccountService {
  constructor() {
    this.useMockData = import.meta.env.VITE_USE_MOCK_DATA === "true" || false;
  }

  async getAccounts(params = {}) {
    if (this.useMockData) {
      return await mockApiService.getAccounts(params);
    }

    try {
      const response = await createRequestWithFallback(
        API_ENDPOINTS.ACCOUNTS.LIST
      )(params);
      return this.normalizeResponse(response);
    } catch (error) {
      return await this.handleFallback(
        () => mockApiService.getAccounts(params),
        error
      );
    }
  }

  async createAccount(accountData) {
    if (this.useMockData) {
      return await mockApiService.createAccount(accountData);
    }

    try {
      this.validateAccountData(accountData);
      const response = await httpClient.post(
        API_ENDPOINTS.ACCOUNTS.CREATE,
        accountData
      );
      return response.data;
    } catch (error) {
      return await this.handleFallback(
        () => mockApiService.createAccount(accountData),
        error
      );
    }
  }

  async updateAccount(id, accountData) {
    if (this.useMockData) {
      return await mockApiService.updateAccount(id, accountData);
    }

    try {
      this.validateAccountData(accountData);
      const response = await httpClient.put(
        `${API_ENDPOINTS.ACCOUNTS.UPDATE}/${id}`,
        accountData
      );
      return response.data;
    } catch (error) {
      return await this.handleFallback(
        () => mockApiService.updateAccount(id, accountData),
        error
      );
    }
  }

  async deleteAccount(id) {
    if (this.useMockData) {
      return await mockApiService.deleteAccount(id);
    }

    try {
      const response = await httpClient.delete(
        `${API_ENDPOINTS.ACCOUNTS.DELETE}/${id}`
      );
      return response.data;
    } catch (error) {
      return await this.handleFallback(
        () => mockApiService.deleteAccount(id),
        error
      );
    }
  }

  async getAccountDetails(id) {
    if (this.useMockData) {
      return await mockApiService.getAccountDetails(id);
    }

    try {
      const response = await httpClient.get(
        `${API_ENDPOINTS.ACCOUNTS.DETAILS}/${id}`
      );
      return response.data;
    } catch (error) {
      return await this.handleFallback(
        () => mockApiService.getAccountDetails(id),
        error
      );
    }
  }

  normalizeResponse(response) {
    if (!response) return [];

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response.accounts && Array.isArray(response.accounts)) {
      return response.accounts;
    }

    if (response.Items && Array.isArray(response.Items)) {
      return response.Items;
    }

    return [response];
  }

  async handleFallback(mockFunction, originalError) {
    try {
      const mockResponse = await mockFunction();
      return {
        ...mockResponse,
        isMockData: true,
        originalError: originalError.message,
      };
    } catch (mockError) {
      throw new Error(`API and mock service failed: ${originalError.message}`);
    }
  }

  validateAccountData(accountData) {
    if (!accountData || typeof accountData !== "object") {
      throw new Error("Account data must be a valid object");
    }

    const requiredFields = ["AccountName", "AccountType"];
    for (const field of requiredFields) {
      if (!accountData[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }
  }
}

export default new AccountService();
